import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, Edit, Save, Visibility, VisibilityOff } from '@mui/icons-material';
import api from '../../services/api';

const BUSINESS_TYPE_OPTIONS = [
  { value: 'jewelry', label: 'Jewelry' },
  { value: 'watches', label: 'Watches' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'grocery', label: 'Grocery' },
  { value: 'automotive', label: 'Car Dealership' },
  { value: 'clothing', label: 'Clothing' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'coffee_shop', label: 'Coffee Shop' },
  { value: 'wine_cellar', label: 'Wine & Cellar' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'bookstore', label: 'Bookstore' },
  { value: 'hair_beauty_salon', label: 'Hair & Beauty Salon' },
  { value: 'aesthetics_clinic', label: 'Aesthetics Clinic' },
  { value: 'gemstone', label: 'Gemstone' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'general', label: 'General' }
];

const defaultForm = {
  id: '',
  type: 'text',
  title: '',
  description: '',
  content: '',
  youtubeUrl: '',
  thumbnailUrl: '',
  category: 'General',
  tags: '',
  audienceMode: 'all',
  businessTypes: [],
  sortOrder: 0,
  isVisible: true
};

const HelpCenterContent = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBusinessType, setFilterBusinessType] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterVisibility, setFilterVisibility] = useState('all');
  const [filterHasVideo, setFilterHasVideo] = useState('all');

  const visibleItems = useMemo(() => {
    if (showDeleted) {
      return items;
    }
    return items.filter(item => item.isDeleted !== true);
  }, [items, showDeleted]);

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return visibleItems.filter((item) => {
      const matchesSearch = !query || [
        item.title,
        item.description,
        item.content,
        item.category,
        Array.isArray(item.tags) ? item.tags.join(' ') : ''
      ]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(query));

      const matchesType = filterType === 'all' || item.type === filterType;

      const matchesVisibility =
        filterVisibility === 'all' ||
        (filterVisibility === 'visible' && item.isVisible !== false) ||
        (filterVisibility === 'hidden' && item.isVisible === false);

      const hasVideoLink = Boolean(String(item.youtubeUrl || '').trim());
      const matchesHasVideo =
        filterHasVideo === 'all' ||
        (filterHasVideo === 'yes' && hasVideoLink) ||
        (filterHasVideo === 'no' && !hasVideoLink);

      const matchesBusinessType =
        filterBusinessType === 'all' ||
        item.audienceMode === 'all' ||
        (Array.isArray(item.businessTypes) && item.businessTypes.includes(filterBusinessType));

      return matchesSearch && matchesType && matchesVisibility && matchesHasVideo && matchesBusinessType;
    });
  }, [visibleItems, searchQuery, filterType, filterVisibility, filterHasVideo, filterBusinessType]);

  const fetchHelpCenter = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/config/help-center');
      if (response.data?.success) {
        const tutorials = response.data.helpCenterConfig?.tutorials || [];
        setItems(tutorials);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load help center configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHelpCenter();
  }, []);

  const clearForm = () => {
    setForm(defaultForm);
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id || '',
      type: item.type || 'text',
      title: item.title || '',
      description: item.description || '',
      content: item.content || '',
      youtubeUrl: item.youtubeUrl || '',
      thumbnailUrl: item.thumbnailUrl || '',
      category: item.category || 'General',
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : '',
      audienceMode: item.audienceMode || 'all',
      businessTypes: Array.isArray(item.businessTypes) ? item.businessTypes : [],
      sortOrder: Number(item.sortOrder || 0),
      isVisible: item.isVisible !== false
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload = {
        tutorial: {
          ...form,
          tags: form.tags
            .split(',')
            .map(tag => tag.trim())
            .filter(Boolean)
        },
        updatedBy: 'admin'
      };

      const response = await api.post('/config/help-center', payload);
      if (response.data?.success) {
        setSuccess(response.data.message || 'Tutorial saved');
        await fetchHelpCenter();
        clearForm();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save tutorial');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async (item) => {
    try {
      setError('');
      setSuccess('');
      const response = await api.post(`/config/help-center/${item.id}/hide`, {
        isVisible: !item.isVisible,
        updatedBy: 'admin'
      });
      if (response.data?.success) {
        setSuccess(response.data.message || 'Tutorial visibility updated');
        await fetchHelpCenter();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update visibility');
    }
  };

  const handleDelete = async (item) => {
    try {
      setError('');
      setSuccess('');
      const response = await api.delete(`/config/help-center/${item.id}`, {
        data: { updatedBy: 'admin' }
      });
      if (response.data?.success) {
        setSuccess(response.data.message || 'Tutorial deleted');
        await fetchHelpCenter();
        if (form.id === item.id) {
          clearForm();
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete tutorial');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Alert severity="info" sx={{ mb: 2 }}>
        Help Center supports shared tutorials for all businesses and business-specific tutorials.
        iOS app downloads only relevant content for the active business type.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={5}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {form.id ? 'Edit Tutorial' : 'Create Tutorial'}
                </Typography>
                <Button size="small" onClick={clearForm} startIcon={<Add />}>
                  New
                </Button>
              </Box>

              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={form.type}
                    label="Type"
                    onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <MenuItem value="text">Text Tutorial</MenuItem>
                    <MenuItem value="video">Video Tutorial</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Title"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  fullWidth
                />

                <TextField
                  label="Description"
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  multiline
                  rows={2}
                  fullWidth
                />

                <TextField
                  label="Category"
                  value={form.category}
                  onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                  fullWidth
                />

                {form.type === 'text' && (
                  <TextField
                    label="Content"
                    value={form.content}
                    onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
                    multiline
                    rows={8}
                    fullWidth
                  />
                )}

                <TextField
                  label="YouTube URL (optional for text, required for video)"
                  value={form.youtubeUrl}
                  onChange={(e) => setForm(prev => ({ ...prev, youtubeUrl: e.target.value }))}
                  fullWidth
                />

                <TextField
                  label="Thumbnail URL (optional)"
                  value={form.thumbnailUrl}
                  onChange={(e) => setForm(prev => ({ ...prev, thumbnailUrl: e.target.value }))}
                  fullWidth
                />

                <TextField
                  label="Tags (comma separated)"
                  value={form.tags}
                  onChange={(e) => setForm(prev => ({ ...prev, tags: e.target.value }))}
                  fullWidth
                />

                <TextField
                  label="Sort Order"
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm(prev => ({ ...prev, sortOrder: parseInt(e.target.value || '0', 10) }))}
                  fullWidth
                />

                <FormControl fullWidth>
                  <InputLabel>Audience</InputLabel>
                  <Select
                    value={form.audienceMode}
                    label="Audience"
                    onChange={(e) => setForm(prev => ({ ...prev, audienceMode: e.target.value }))}
                  >
                    <MenuItem value="all">All Businesses (Shared)</MenuItem>
                    <MenuItem value="business_types">Specific Business Types</MenuItem>
                  </Select>
                </FormControl>

                {form.audienceMode === 'business_types' && (
                  <FormControl fullWidth>
                    <InputLabel>Business Types</InputLabel>
                    <Select
                      multiple
                      value={form.businessTypes}
                      onChange={(e) => setForm(prev => ({ ...prev, businessTypes: e.target.value }))}
                      input={<OutlinedInput label="Business Types" />}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      {BUSINESS_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Checkbox checked={form.businessTypes.includes(option.value)} />
                          <ListItemText primary={option.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isVisible}
                      onChange={(e) => setForm(prev => ({ ...prev, isVisible: e.target.checked }))}
                    />
                  }
                  label="Visible in app"
                />

                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : (form.id ? 'Update Tutorial' : 'Create Tutorial')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Tutorials</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showDeleted}
                      onChange={(e) => setShowDeleted(e.target.checked)}
                    />
                  }
                  label="Show deleted"
                />
              </Box>

              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Search tutorials"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by title, category, tags, content..."
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select value={filterType} label="Type" onChange={(e) => setFilterType(e.target.value)}>
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="text">Text</MenuItem>
                      <MenuItem value="video">Video</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Visibility</InputLabel>
                    <Select value={filterVisibility} label="Visibility" onChange={(e) => setFilterVisibility(e.target.value)}>
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="visible">Visible</MenuItem>
                      <MenuItem value="hidden">Hidden</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Has Video</InputLabel>
                    <Select value={filterHasVideo} label="Has Video" onChange={(e) => setFilterHasVideo(e.target.value)}>
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Business Type</InputLabel>
                    <Select
                      value={filterBusinessType}
                      label="Business Type"
                      onChange={(e) => setFilterBusinessType(e.target.value)}
                    >
                      <MenuItem value="all">All (including shared)</MenuItem>
                      {BUSINESS_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Stack spacing={1.5}>
                {filteredItems.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No tutorials found.
                  </Typography>
                )}

                {filteredItems
                  .slice()
                  .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                  .map((item) => (
                    <Card
                      key={item.id}
                      variant="outlined"
                      sx={{
                        opacity: item.isDeleted ? 0.5 : 1,
                        borderColor: form.id === item.id ? 'primary.main' : undefined
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                              <Chip size="small" label={item.type === 'video' ? 'Video' : 'Text'} />
                              <Chip size="small" label={item.category || 'General'} />
                              <Chip size="small" color={item.isVisible ? 'success' : 'default'} label={item.isVisible ? 'Visible' : 'Hidden'} />
                              {String(item.youtubeUrl || '').trim() && <Chip size="small" color="info" label="Has Video Link" />}
                              {item.audienceMode === 'all' ? (
                                <Chip size="small" color="primary" label="All Businesses" />
                              ) : (
                                <Chip size="small" color="secondary" label={`Targeted (${(item.businessTypes || []).length})`} />
                              )}
                              {item.isDeleted && <Chip size="small" color="error" label="Deleted" />}
                            </Stack>

                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {item.title}
                            </Typography>
                            {item.description && (
                              <Typography variant="body2" color="text.secondary">
                                {item.description}
                              </Typography>
                            )}
                          </Box>

                          <Stack direction="row" spacing={0.5}>
                            <Button size="small" onClick={() => handleEdit(item)} startIcon={<Edit />}>
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color={item.isVisible ? 'warning' : 'success'}
                              onClick={() => handleToggleVisibility(item)}
                              startIcon={item.isVisible ? <VisibilityOff /> : <Visibility />}
                              disabled={item.isDeleted}
                            >
                              {item.isVisible ? 'Hide' : 'Show'}
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleDelete(item)}
                              startIcon={<Delete />}
                              disabled={item.isDeleted}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Box>

                        {item.audienceMode === 'business_types' && (
                          <>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="caption" color="text.secondary">
                              Business types: {(item.businessTypes || []).join(', ')}
                            </Typography>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HelpCenterContent;
